export enum UserPropertyType {
  DirectoryCustom = "directory_custom",
  DirectoryRaw = "directory_raw",
  LastUsersFilter = "last_users_filter",
  LastTeamsFilter = "last_teams_filter",
  LastOfficesFilter = "last_offices_filter",
  DesignSystemVersion = "design_system_version"
}

export type DesignSystemVersionValues = "v1" | "v2";

export interface IUserSettings {
  organization_id: string;
  user_id: string;
  type: UserPropertyType;
  value: string;
  created?: string;
  updated?: string;
}

export type ResUserSettings = {
  elements: IUserSettings[];
  cursor: string;
  total: number;
};

export type NotificationSettings = {
  email: {
    enable_admin_pending_schedule_created: boolean;
    admin_pending_schedule_created_offices: string[];
    enable_auto_cancellation: boolean;
    enable_checkin_reminder: boolean;
    enable_pending_schedule_approved: boolean;
    enable_pending_schedule_denied: boolean;
    enable_tickets_comments: boolean;
    enable_tickets_updated: boolean;
    enable_tickets_created_for_manager: boolean;
    enable_avigilon_credentials_invitation: boolean;
  };
  slack: {
    enable_tickets_comments: boolean;
    enable_tickets_updated: boolean;
    enable_tickets_created_for_manager: boolean;
    enable_checkin_reminder: boolean;
    enable_auto_cancellation: boolean;
    enable_pending_schedule_denied: boolean;
    enable_pending_schedule_approved: boolean;
    enable_admin_pending_schedule_created: boolean;
    admin_pending_schedule_created_offices: string[];
  };
  microsoft_teams: {
    enable_tickets_comments: boolean;
    enable_tickets_updated: boolean;
    enable_tickets_created_for_manager: boolean;
    enable_checkin_reminder: boolean;
    enable_auto_cancellation: boolean;
    enable_pending_schedule_denied: boolean;
    enable_pending_schedule_approved: boolean;
    enable_admin_pending_schedule_created: boolean;
    admin_pending_schedule_created_offices: string[];
  };
  push: {
    enable_tickets_comments: boolean;
    enable_tickets_updated: boolean;
    enable_tickets_created_for_manager: boolean;
    enable_checkin_reminder: boolean;
    enable_auto_cancellation: boolean;
    enable_pending_schedule_denied: boolean;
    enable_pending_schedule_approved: boolean;
    enable_admin_pending_schedule_created: boolean;
    admin_pending_schedule_created_offices: string[];
  };
  sms: {
    enable_avigilon_credentials_invitation: boolean;
  };
};

/**
 * A private, named filter preset for one list screen. `filters` is opaque to
 * the backend: it is stored and returned unchanged, and the client replays it
 * against the list endpoints, so its shape is a contract between clients.
 */
export interface SavedView<TFilters = Record<string, unknown>> {
  /** Client-supplied uuid; must be unique across the whole `views` object. */
  id: string;
  name: string;
  /** Always present, may be an empty string. */
  description: string;
  filters: TFilters;
}

/** Saved views keyed by app section then sub-area, e.g. `views.tickets.dashboard`. */
export type SectionViews = Record<string, Record<string, SavedView[]>>;

/** Backend limits (core `pkg/domain/settings/user_views.go`); exceeding any of them returns `invalid_view`. */
export const SAVED_VIEW_LIMITS = {
  maxPerArea: 50,
  maxNameLength: 120,
  maxDescriptionLength: 500,
  maxFiltersBytes: 8 * 1024
} as const;

export type ResUserSettingsCore = {
  settings: {
    design_system_version: DesignSystemVersionValues;
    notification_settings: NotificationSettings;
    /**
     * Personal week start override (0=Sunday..6=Saturday).
     * null/undefined means the user inherits the organization setting.
     */
    week_starts_on?: number | null;
    /** Saved filter presets, private to the user. Empty object when none are stored. */
    views?: SectionViews;
  };
};
